<template>
	<div v-if="!!userStore?.currBusiness && !userStore.showStripeButton() && !userStore?.currBusiness?.stripe_account_details_submitted" class="card-link">
		<CardSlashed class="mb-30">
			<template #left>
				<div class="col-12 centered card-link-body">
					<div class="row centered flex-start" v-if="userStore.currBusiness">
						<p v-if="(userStore.currBusiness.stripe_account && userStore.currBusiness.config_payments)">
							{{getString('stripe_done')}}
						</p>
						<p v-else>
							<span class="txt-white-color" v-html="getString('we_use_stripe')"></span>
							<b class="txt-white-color" v-if="!userStore.currBusiness.config_menu || !userStore.currBusiness.config_settings"><br/><br/>{{getString('stripe_subtitle', [userStore.menuWord])}}</b>
						</p>
					</div>
					<div class="centered pt-20 card-link-actions" v-if="userStore.currBusiness">
						<AppButton
							outline
							inverted
							@click="connectStripe"
							:loading="loading"
							:text="getString(
							userStore.currBusiness.stripe_account && !userStore.currBusiness.config_payments ?
							'continue_with_stripe' :
							'stripe_link'
						)"
						/>
					</div>
				</div>
			</template>
			<template #right>
				<div class="wrapper-img">
					<picture>
						<source type="image/webp" srcset="https://img.qromo.io/img/banenr-stripe.webp">
						<img src="https://img.qromo.io/img/banenr-stripe.png" alt="product image">
					</picture>
				</div>
			</template>
		</CardSlashed>
	</div>
	<div class="banner-deadline" v-if="bannerDeadline" :class="{incomplete: bannerDeadline === 'incomplete', warning: bannerDeadline === 'warning', blocked: bannerDeadline === 'blocked', dark: userStore.darkMode}" @click="deadlineStripeDashboard">
		<div class="banner-deadline-icon" v-html="svgStore.get(bannerDeadline === 'incomplete' ? 'iconInfo' : 'iconWarning')"></div>
		<div class="banner-deadline-text">
			{{ bannerDeadlineMessage }}
		</div>
		<div class="banner-deadline-cta">
			<AppButton small :text="getString('stripe_express_update_info_cta')" :loading="loading" inverted />
		</div>
	</div>
	<div class="banner-deadline warning" :class="{dark: userStore.darkMode}" v-if="showMigrationBanner" @click="openMigrationPopup">
		<div class="banner-deadline-icon" v-html="svgStore.get('iconWarning')"></div>
		<div class="banner-deadline-text">
			{{ getString('stripe_express_migration') }}
		</div>
		<div class="banner-deadline-cta">
			<AppButton :text="getString('stripe_express_migration_cta')" :loading="loadingMigration" inverted />
		</div>
	</div>
	<TransfersTable @failed-fetch="handleFetchError" />
	<TransactionsTable @failed-fetch="handleFetchError" class="mt-30" />
        <AppPopup
                :title="getString('stripe_enable_one_day_payouts')"
                :body-text="getString('stripe_enable_one_day_payouts_text')"
                :buttons="delayOneDayPopupButtons"
                ref="confirm-one-day-delay"
        />
        <AppPopup
                :title="getString('warning')"
                :body-text="getString('stripe_express_migration_popup')"
                :buttons="migrationPopupButtons"
                ref="confirm-migrate-custom"
        />

	<AppBottomSheet :active="administrationStore.showStripeBottomSheet" @toggling-popup="toggleStripeBottomSheet" :force-width="bottomSheetWidth">
		<ModalHeader :title="getString('stripe')" :button-text="saveButtonPayoutDelay" @clickButton="bottomSheetSavePayoutDelay"/>
		<AppList :items="stripeBottomSheetItems">
			<template #stripe-account-id>
				<div class="stripe-account-id-row" @click="copyStripeAccountId" @mouseenter="activeStripeIdCloud = true" @mouseleave="activeStripeIdCloud = false">
					<p class="stripe-account-id-title">{{ getString('stripe_account_id') }}</p>
					<div class="stripe-account-id-value">
						<p>{{ userStore.currBusiness.stripe_account }}</p>
						<AppInPlaceCloud :active="activeStripeIdCloud" force-top>
							<div class="centered pl-20 pr-20">
								<p>{{ stripeIdCloudMessage }}</p>
							</div>
						</AppInPlaceCloud>
					</div>
				</div>
			</template>
		</AppList>
	</AppBottomSheet>
	<AppBottomSheet :active="showStripeOnboarding" @toggling-popup="toggleStripeOnboardingSheet" :force-width="bottomSheetWidth">
		<div id="stripe-onboarding-container"></div>
	</AppBottomSheet>
	<StripeDashboardCustom v-model:active="showStripeDashboard" />
	<StripeInstantPayoutBottomSheet :instant-available-amount="instantAvailableAmount" :active="showInstantPayoutBottomSheet"
									:instant-payout-destination="instantPayoutDestination" @toggling-popup="toggleInstantPayoutBottomSheet"/>
</template>

<script>
import AppSelect from "@/shared/components/AppSelect.vue";
import CardSlashed from "@/console-new/components/shared/CardSlashed.vue";
import AppButton from "@/shared/components/AppButton.vue";
import {useUserStore, useAdministrationStore, useFunnelStore} from "@/console-new/stores";
import {useLanguageStore, useScriptStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import API from "@/shared/helpers/API";
import AppPopup from "@/shared/components/AppPopup.vue";
import TransfersTable from "@/console-new/components/config/administration/TransfersTable.vue";
import TransactionsTable from "@/console-new/components/shared/TransactionsTable.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppList from "@/shared/components/AppList.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import TouchNumberInput from "@/console-new/components/shared/TouchNumberInput.vue";
import StripeInstantPayoutBottomSheet from "@/console-new/components/config/administration/StripeInstantPayoutBottomSheet.vue";
import StripeDashboardCustom from "@/console-new/components/config/administration/StripeDashboardCustom.vue";
import AppInPlaceCloud from "@/shared/components/AppInPlaceCloud.vue";
import { useRoute, useRouter } from 'vue-router';
import {formatDateMMDDHHmm, getLocalDateFromServer} from "@/shared/helpers/dates";

export default {
	setup() {
		const userStore = useUserStore();
		const administrationStore = useAdministrationStore();
		const funnelStore = useFunnelStore();
		const scriptStore = useScriptStore();
		const notificationStore = useNotificationStore();
		const svgStore = useSvgStore();
		const route = useRoute();
		const router = useRouter();
		return {userStore, svgStore, administrationStore, getString: useLanguageStore().getString, showNotification: notificationStore.showNotification, funnelStore, scriptStore, route, router};
	},
	mixins: [WindowWidth],
	data() {
		return {
			DELAY_VALUES: [1,2,3,7],
			loading: false,
			loadingDelay: false,
			loadingIdentitySession: false,
			processingIdentityVerification: false,
			selectedStripeDelayIndex: null,
			showInstantPayoutBottomSheet: false,
			instantAvailableAmount: 0,
			instantPayoutDestination: null,
			showStripeOnboarding: false,
			embeddedOnboardingComponent: null,
			showStripeDashboard: false,
			loadingMigration: false,
			activeStripeIdCloud: false,
			stripeIdCloudMessage: this.getString('copy'),
		}
        },
	name: "StripeTablesContainer" ,
	components: {
		StripeDashboardCustom, AppInPlaceCloud,
		TouchNumberInput, AppNumberInput, AppBottomSheet, AppList, ModalHeader, TransactionsTable, TransfersTable, AppPopup, AppButton, CardSlashed, AppSelect, StripeInstantPayoutBottomSheet},
	computed: {
		bannerDeadline() {
			if (this.userStore.currBusiness && this.userStore.currBusiness.stripe_account && this.userStore.currBusiness.stripe_account_type === "custom" && this.userStore.currBusiness.stripe_account_details_submitted && (this.userStore.currBusiness.stripe_account_deadline || this.userStore.currBusiness.stripe_account_highest_requirement)) {
				let currDeadline = this.userStore.currBusiness.stripe_account_deadline;
				if(currDeadline) {
					//per supportare l'attuale base dati
					return currDeadline <= new Date() ? "blocked" : "warning";
				} else {
					let highestRequirement = this.userStore.currBusiness.stripe_account_highest_requirement;
					if(highestRequirement === 'past_due') {
						return "blocked";
					} else if(highestRequirement === 'currently_due') {
						return "warning";
					} else if(highestRequirement === 'eventually_due') {
						return "incomplete";
					}
				}
			}
			return false;
		},
		showMigrationBanner() {
			return this.userStore.currBusiness && this.userStore.currBusiness.stripe_account && this.userStore.currBusiness.stripe_account_type === 'express';
		},
		bannerDeadlineMessage() {
			if(this.bannerDeadline === 'blocked') {
				return this.getString("stripe_account_blocked");
			} else if(this.bannerDeadline === 'warning' && this.userStore.currBusiness.stripe_account_deadline) {
				return this.getString("stripe_account_date_warning", [formatDateMMDDHHmm(this.userStore.currBusiness.stripe_account_deadline)]);
			} else if(this.bannerDeadline === 'warning') {
				return this.getString("stripe_account_warning");
			} else if(this.bannerDeadline === 'incomplete') {
				return this.getString("stripe_account_incomplete");
			}
			return '';
		},
		delayOneDayPopupButtons() {
			return [
				{text: this.getString('cancel'), negative: true, loading: this.loadingDelay},
				{text: this.getString('ok'), callback: () => this.savePayoutDelay(1), loading: this.loadingDelay}
			];
		},
		migrationPopupButtons() {
			return [
				{text: this.getString('cancel'), negative: true, loading: this.loadingMigration},
				{text: this.getString('ok'), callback: this.confirmMigration, loading: this.loadingMigration}
			];
		},
		showIdentityVerificationSection() {
			let currBus = this.userStore.currBusiness;
			return currBus && currBus.stripe_account && currBus.config_payments && ['small', 'custom', 'big'].includes(currBus.tier);
		},
		showIdentityVerificationButton() {
			return this.showIdentityVerificationSection && !this.userStore.currBusiness.verified_user_id;
		},
		showPayoutDelaySelect() {
			return this.showIdentityVerificationSection && this.userStore.currBusiness.payout_interval !== 'manual' && this.userStore.currBusiness.verified_user_id;
		},
		stripeBottomSheetItems() {
			let items = [];

			if(this.userStore.currBusiness?.stripe_account) {
				items.push({
					title: this.getString('stripe_account_id'),
					customSlot: 'stripe-account-id'
				});
			}

			if(this.userStore.showStripeButton()) {
				items.push({
					title: this.getString('stripe_dashboard'),
					buttonTitle: this.getString('go_to_stripe'),
					callbackButton: this.stripeDashboard,
					buttonLoading: this.loading
				});
			}
			else if(this.userStore.currBusiness && this.userStore.currBusiness.stripe_account && !this.userStore.currBusiness.config_payments) {
				items.push({
					title: this.getString('stripe_dashboard'),
					buttonTitle: this.getString('continue_with_stripe'),
					callbackButton: this.connectStripe,
					buttonLoading: this.loading
				});
			}
			else if(this.userStore.currBusiness){
				items.push({
					title: this.getString('stripe_dashboard'),
					buttonTitle: this.getString('stripe_link'),
					callbackButton: this.connectStripe,
					buttonLoading: this.loading
				});
			}

			if(this.showIdentityVerificationButton) {
				items.push({
					title: this.getString('stripe_identity_btn'),
					explain: this.userStore.optionalUserVerificationForBusiness() ?
						this.getString('stripe_identity_optional') :
						this.getString('stripe_identity_mandatory'),
					buttonTitle: this.getString('verify'),
					callbackButton: this.openStripeIdentityVerificationSessionModal,
					buttonLoading: this.loading || this.loadingIdentitySession
				});
			}
			if(this.showPayoutDelaySelect) {
				items.push({
					title: this.getString('stripe_payouts_delay'),
					selectValues: [this.getString('one_day'), this.getString('x_days', ['2']), this.getString('x_days', ['3']), this.getString('x_days', ['7'])],
					selectedIndex: this.selectedStripeDelayIndex,
					callbackSelect: i => this.selectedStripeDelayIndex = i
				});
				items.push({
					title: this.getString('stripe_instant_payout'),
					buttonTitle: this.getString('stripe_make_withdrawal'),
					buttonLoading: this.loading,
					callbackButton: () => {
						this.loading = true;
						API.init().checkBalance(this.userStore.currBusinessId, (res) => {
							this.loading = false;
							let netInstantPayoutData = res?.instant_available?.[0]?.net_available?.[0];
							if(netInstantPayoutData?.amount && netInstantPayoutData.amount > 0) {
								this.instantAvailableAmount = netInstantPayoutData.amount;
								this.instantPayoutDestination = netInstantPayoutData.destination;
								this.showInstantPayoutBottomSheet = true;
							} else {
								this.showNotification('stripe-instant-payout-not-eligible');
							}
						});
					}
				});
			}

			return items;
		},
		saveButtonPayoutDelay() {
			return this.showPayoutDelaySelect ? this.getString('save') : null;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else {
				return "960px";
			}
		}
	},
	methods: {
		deadlineStripeDashboard() {
			if(this.userStore.currBusiness.stripe_account_type === "express") {
				this.stripeDashboard();
			} else {
				this.administrationStore.stripeConnectInstanceBusinessId = null;
				this.showStripeDashboard = true;
			}
		},
		stripeDashboard() {
			if(this.userStore.currBusiness.stripe_account_type === "express") {
				this.loading = true;
				this.administrationStore.getStripeDashboardLink(url => {
					if(url) {
						localStorage.setItem('expressDashboardNavigatedToo', true);
						window.location.replace(url);
					} else {
						this.showNotification('generic-fail');
					}
					this.loading = false;
				});
			} else {
				this.showStripeDashboard = true;
			}
		},
		connectStripe() {
			this.loading = true;
			this.administrationStore.getStripeConnectInstance("onboarding", connectInstance => {

				this.loading = false;
				if(connectInstance) {

					this.showStripeOnboarding = true;
					const container = document.getElementById('stripe-onboarding-container');
					this.embeddedOnboardingComponent = connectInstance.create("account-onboarding");
					this.embeddedOnboardingComponent.setOnExit(() => {
						this.showStripeOnboarding = false;
						this.loadingConnectStripe = true;
						this.userStore.refreshBusiness(this.userStore.currBusinessId, () => {
							this.loadingConnectStripe = false;
						});
					});
					container.appendChild(this.embeddedOnboardingComponent);

				} else {
					this.showStripeOnboarding = false;
					this.showNotification('generic-fail');
				}
			});
		},
		openMigrationPopup() {
			this.$refs['confirm-migrate-custom'].toggleStatus(true);
		},
		confirmMigration() {
			this.$refs['confirm-migrate-custom'].toggleStatus(false);
			this.loadingMigration = true;
			this.administrationStore.migrateStripeToCustom((account, details) => {
				this.loadingMigration = false;
				if(account) {
					this.connectStripe();
				} else {
					this.showNotification('generic-fail');
				}
				});
		},
		openStripeIdentityVerificationSessionModal() {
			this.loadingIdentitySession = true;

			this.userStore.openStripeIdentityVerificationSessionModal(result => {
				if(result === 'OK') {
					this.loadingIdentitySession = false;
					this.processingIdentityVerification = false;
				}
				else if(result === 'Processing') {
					this.loadingIdentitySession = false;
					this.processingIdentityVerification = true;
				}
				else if(result === 'KO') {
					this.loadingIdentitySession = false;
				}
			});
		},
		clickSavePayoutDelay() {
			let newDelay = this.DELAY_VALUES[this.selectedStripeDelayIndex];
			if(!newDelay) {
				this.showNotification("generic-fail");
				return
			}
			if(newDelay === 1) {
				this.$refs['confirm-one-day-delay'].toggleStatus(true);
			} else {
				this.savePayoutDelay(newDelay)
			}
		},
		savePayoutDelay(newDelay) {
			this.$refs['confirm-one-day-delay'].toggleStatus(false);
			if(!this.DELAY_VALUES.includes(newDelay)) {
				this.showNotification("generic-fail");
				return
			}
			this.loadingDelay = true;
			API.init().updateBusinessPayoutDelay(this.userStore.currBusinessId, newDelay, resp => {
				this.loadingDelay = false;
				if(resp && resp.result === "OK") {
					this.showNotification("stripe-payout-delay-success");
					this.userStore.currBusiness.payout_delay = newDelay;
				} else {
					this.showNotification("generic-fail");
				}
			});
		},
		handleFetchError(details) {
			if(details && details !== 'not_connected') {
				let msg = details === 'auth_denied' ? 'auth-denied-error' : 'generic-fail';
				if(details === 'refund_limit_exceeded') {
					msg = 'refund-limit-exceeded-error';
				}
				this.showNotification(msg);
			}
		},
		toggleStripeBottomSheet(active) {
			if(!active) {
				this.administrationStore.showStripeBottomSheet = false;
			}
		},
		toggleStripeOnboardingSheet(active) {
			if(!active) {
				if (this.embeddedOnboardingComponent) {
					const container = document.getElementById('stripe-onboarding-container');
					if (container && container.firstChild) {
						container.removeChild(container.firstChild);
					}
					this.embeddedOnboardingComponent = null;
				}
				this.showStripeOnboarding = false;
			}
		},
		bottomSheetSavePayoutDelay() {
			this.administrationStore.showStripeBottomSheet = false;
			this.clickSavePayoutDelay();
		},
		toggleInstantPayoutBottomSheet(active) {
			if(!active) {
				this.showInstantPayoutBottomSheet = false;
			}
		},
		copyStripeAccountId() {
			navigator.clipboard.writeText(this.userStore.currBusiness.stripe_account);
			this.stripeIdCloudMessage = this.getString('copied');
			setTimeout(() => {
				this.stripeIdCloudMessage = this.getString('copy');
			}, 1500);
		}
	},
	mounted() {
		if(this.userStore?.currBusiness?.payout_delay) {
			this.selectedStripeDelayIndex = this.DELAY_VALUES.indexOf(this.userStore.currBusiness.payout_delay);
		}
		if(!this.userStore.businessUsers || !Object.keys(this.userStore.businessUsers).length) {
			this.userStore.getBusinessUsers();
		}

		if (this.route.path.endsWith('/stripe/dashboard')) {
			this.showStripeDashboard = true;
		}
	},
	watch: {
		'userStore.currBusiness.payout_delay': function(newVal) {
			if(newVal) {
				this.selectedStripeDelayIndex = this.DELAY_VALUES.indexOf(newVal);
			}
		},
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.userStore.getBusinessUsers();
			}
		},
	}
}
</script>

<style scoped lang="scss">
.card-link {

	.card-link-title {
		z-index: 3;
		@include centered;
		color: var(--color-txt-white);
		max-width: 400px;
		padding: 0 15px;
		text-align: center;

		h2 {
			width: 100%;
			font-size: 28px;
			text-align: center;
			color: var(--color-txt-white);
		}
		@media (max-width: 500px) {
			h2 {font-size: 24px;}
		}
	}
	.card-link-body {
		justify-content: flex-start;
		z-index: 3;

		.card-link-actions {
			width: 100%;
			justify-content: flex-start;
		}
		p, span {
			width: 100%;
			font-size: 14px;
			line-height: 18px;
			text-align: left;
		}

		.txt-white-color {
			color: var(--color-txt-white);
		}

		.verification-button-container {
			border-top: 1px solid var(--color-alpha-4);
		}

		@media (max-width: 500px) {
			p {font-size: 12px;}
		}
	}
	:deep(.card-container) {
		.card-body {
			padding: 0;
			.slashed-card-container {
				position: relative;
				background: linear-gradient(198deg, #635bff 0%, #4942aa 100%);
				@media (max-width: 550px) {
					flex-direction: column-reverse;
				}
				.slashed-left {
					width: 60%;
					padding: 20px;
					position: unset;
					@media (max-width: 550px) {
						width: 100%;
						padding-top: 0;
					}
				}
				.slashed-right {
					width: 40%;
					padding: 20px;
					position: unset;
					@media (max-width: 550px) {
						width: 100%;
					}
				}
			}
			h2 {
				color: var(--color-txt-white);
			}
		}
	}
}
#stripe-onboarding-container {
	min-height: 30vh;
	padding: 3px 4px;
	overflow: hidden;
	margin: 0 auto;
}
.banner-deadline {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	padding: 10px 20px;
	border-radius: 13px;
	margin-bottom: 30px;
	cursor: pointer;
	border: 1px solid;

	&:deep(svg) {
		width: 20px;
		height: 20px;
		margin-right: 10px;

		.fill {
			fill: #e83d15;
		}

		.stroke {
			stroke: #e83d15;
		}
	}

	&.warning {
		background-color: rgba(241, 171, 1, 0.15);
		border-color: #f1ab01;

		&:deep(svg) {
			.fill {
				fill: #f1ab01;
			}

			.stroke {
				stroke: #f1ab01;
			}
		}
	}

	&.incomplete {
		background-color: rgba(#afaeae, 0.15);
		border-color: #afaeae;

		&:deep(svg) {
			.fill {
				fill: #afaeae;

			}

			.stroke {
				stroke: #afaeae;
			}
		}
	}

	&.blocked {
		background-color: rgba(232, 61, 21, 0.15);
		border-color: #e83d15;
	}

	&.dark {
		&.warning {
			background-color: rgba(242, 214, 15, 0.15);
			border-color: #f2d60f;

			&:deep(svg) {
				.fill {
					fill: #f2d60f;
				}

				.stroke {
					stroke: #f2d60f;
				}
			}
		}

		&.incomplete {
			background-color: rgba(#808080, 0.15);
			border-color: #808080;

			&:deep(svg) {
				.fill {
					fill: #808080;
				}

				.stroke {
					stroke: #808080;
				}
			}
		}
	}

	.banner-deadline-text {
		font-size: 14px;
		line-height: 18px;
		flex-grow: 1;
		padding-right: 10px;
	}

	.banner-deadline-cta {
		margin-left: auto;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	@media (max-width: 600px) {
		padding: 10px;
		flex-wrap: wrap;
		justify-content: flex-end;

		.banner-deadline-text {
			max-width: calc(100% - 40px);
			margin-left: auto;
			padding-right: 0;
		}
		.banner-deadline-cta {
			margin-top: 10px;
			width: 100%;
			justify-content: flex-end;
		}

	}
}

.banner-migrate {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 20px;
        border-radius: 13px;
        margin-bottom: 30px;
        background-color: rgba(253, 199, 0, 0.65);

        .banner-migrate-text {
                color: var(--color-txt-white);
                font-size: 14px;
                line-height: 18px;
                margin-right: 10px;
        }
}
.wrapper-img {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	picture {
		img {
			width: 100%;
			height: 100%;
			max-height: 220px;
			object-fit: contain;
			filter: invert(1);
			@media (max-width: 550px) {
				max-height: 230px;
			}
		}
	}
}

.dark {
	.wrapper-img {
		picture {
			img {
				filter: unset;
			}
		}
	}
}
.stripe-account-id-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	cursor: pointer;
	position: relative;

	.stripe-account-id-title {
		font-size: 16px;
	}

	.stripe-account-id-value {
		position: relative;
		margin-left: auto;
		user-select: none;

		p {
			font-size: 16px;
			text-align: right;
			color: var(--color-primary);
		}
	}

	@media (max-width: 560px) {
		.stripe-account-id-title, .stripe-account-id-value p {
			font-size: 14px;
		}
	}
}
</style>
